import React, { useEffect, useState } from 'react';
import Icon from '../Icon';
import api from '../../lib/api';
import { panelCls, inputCls, primaryBtn, Field, SectionHead, Note, Stat } from './ui';

const CALCULATORS = [
  { key: 'bmi', title: 'BMI', explain: 'A quick ratio of your weight to your height. It is a rough screening number, not a verdict on your health.' },
  { key: 'whtr', title: 'Waist-to-height ratio', explain: 'Compares your waist to your height. Many guidelines suggest keeping it below 0.5.' },
  { key: 'bmr', title: 'Resting energy (BMR)', explain: 'Roughly how much energy your body uses at complete rest, just keeping you going.' },
  { key: 'tdee', title: 'Daily energy needs', explain: 'Your resting energy adjusted for how active your days usually are.' },
  { key: 'weight_range', title: 'Reference weight range', explain: 'The weight range that matches a BMI of 18.5–24.9 for your height.' },
  { key: 'body_fat', title: 'Body fat estimate', explain: 'An estimate from your BMI, age and sex. Body scans and calipers are more accurate.' },
  { key: 'hr_zones', title: 'Heart-rate zones', explain: 'Rough training zones based on your age, and your resting heart rate if you added it.' },
];

export default function CalculatorsPanel() {
  const [inputs, setInputs] = useState({});
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post('/me/calculators', payload || {});
      setInputs(data.inputs || {});
      setResults(data.results);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { run({}); /* uses saved metrics */ }, []);

  const setNum = (k) => (e) => {
    const raw = e.target.value;
    setInputs((i) => ({ ...i, [k]: raw === '' ? null : Number(raw) }));
  };

  const recalc = () => {
    const payload = {};
    ['height_cm', 'weight_kg', 'waist_cm', 'age', 'resting_heart_rate'].forEach((k) => {
      if (inputs[k] != null && inputs[k] !== '') payload[k] = Number(inputs[k]);
    });
    if (inputs.sex) payload.sex = inputs.sex;
    if (inputs.activity_level) payload.activity_level = inputs.activity_level;
    run(payload);
  };

  const r = results || {};
  const range = r.reference_weight_range_kg;

  return (
    <div className={`${panelCls} ph-no-capture`} data-testid="calculators-panel">
      <SectionHead eyebrow="Estimates" title="Health Calculators" />
      <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg max-w-2xl leading-relaxed">
        These use the details you saved in My Health Metrics, so you don’t have to type them again. You can tweak any
        number below to see how the results change — tweaks here don’t overwrite your saved details.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-space-md mb-space-lg" data-testid="calculator-inputs">
        <Field label="Height (cm)" htmlFor="c-h">
          <input id="c-h" data-testid="calc-height" type="number" className={inputCls} value={inputs.height_cm ?? ''} onChange={setNum('height_cm')} />
        </Field>
        <Field label="Weight (kg)" htmlFor="c-w">
          <input id="c-w" data-testid="calc-weight" type="number" step="0.1" className={inputCls} value={inputs.weight_kg ?? ''} onChange={setNum('weight_kg')} />
        </Field>
        <Field label="Waist (cm)" htmlFor="c-waist">
          <input id="c-waist" data-testid="calc-waist" type="number" step="0.1" className={inputCls} value={inputs.waist_cm ?? ''} onChange={setNum('waist_cm')} />
        </Field>
        <Field label="Age" htmlFor="c-age">
          <input id="c-age" data-testid="calc-age" type="number" className={inputCls} value={inputs.age ?? ''} onChange={setNum('age')} />
        </Field>
        <Field label="Resting heart rate" htmlFor="c-rhr">
          <input id="c-rhr" data-testid="calc-rhr" type="number" className={inputCls} value={inputs.resting_heart_rate ?? ''} onChange={setNum('resting_heart_rate')} />
        </Field>
      </div>

      <button type="button" data-testid="calc-run" onClick={recalc} disabled={busy} className={primaryBtn}>
        <Icon name="calculate" className="text-[18px]" />
        <span>{busy ? 'Working it out…' : 'Update results'}</span>
      </button>

      {r.missing_for_full_results?.length ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-md" data-testid="calc-missing">
          Add your {r.missing_for_full_results.map((m) => m.field.replace('_cm', '').replace('_kg', '').replace('_', ' ')).join(', ')} to unlock every result.
        </p>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-md mt-space-lg" data-testid="calculator-results">
        <Stat label="BMI" value={r.bmi ?? '—'} sub={r.bmi_band ? `This is ${r.bmi_band}.` : CALCULATORS[0].explain} accent />
        <Stat label="Waist-to-height ratio" value={r.waist_to_height_ratio ?? '—'} sub={CALCULATORS[1].explain} />
        <Stat label="Resting energy (BMR)" value={r.bmr_kcal ? `${r.bmr_kcal} kcal` : '—'} sub={CALCULATORS[2].explain} />
        <Stat label="Daily energy needs" value={r.tdee_kcal ? `${r.tdee_kcal} kcal` : '—'} sub={CALCULATORS[3].explain} accent />
        <Stat label="Reference weight range" value={range ? `${range[0]}–${range[1]} kg` : '—'} sub={CALCULATORS[4].explain} />
        <Stat label="Body fat estimate" value={r.body_fat_percent ? `${r.body_fat_percent}%` : '—'} sub={CALCULATORS[5].explain} />
      </div>

      {r.heart_rate_zones?.length ? (
        <div className="mt-space-lg" data-testid="calc-hr-zones">
          <h4 className="font-title-md text-title-md uppercase tracking-wider text-outline mb-space-sm">Heart-rate zones</h4>
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">{CALCULATORS[6].explain} Estimated maximum: {r.max_heart_rate} bpm.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-space-sm">
            {r.heart_rate_zones.map((z) => (
              <div key={z.label} className="p-space-md rounded-lg bg-surface border border-surface-container">
                <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant block">{z.label}</span>
                <p className="font-title-md text-title-md font-bold text-outline mt-1">{z.low_bpm}–{z.high_bpm}</p>
                <span className="font-body-sm text-[12px] text-on-surface-variant">bpm</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-space-lg">
        <Note testId="calculators-disclaimer">
          Every number here is an estimate worked out from what you entered. It’s general information to help you
          understand your body — it isn’t a diagnosis and it doesn’t replace professional medical advice.
        </Note>
      </div>
    </div>
  );
}
